import { HttpClient, HttpParams, HttpResponse } from '@angular/common/http';
import { IRequestResult, IResult } from 'app/common/interfaces';
import { AbstractService } from 'app/services/abstract.service';
import { environment as env } from '../../../../../../environments/environment.prod';
import { Injectable } from '@angular/core';
import { Observable, forkJoin } from 'rxjs';
import {
  buildQuery,
  CombinedFilter,
  where,
  andWhere,
  Ops,
  page,
  orderBy,
  OrderVariant,
  noop,
  orWhere
} from '@albatrosdeveloper/ave-utils-npm/lib/utils/query.util';
import AddressAttributes from '@albatrosdeveloper/ave-models-package/lib/schemas/common/embedded/address/address.entity';
import { ICreateBusinessPartnerAddress } from './interfaces/create-business-partner-address.interface';
import { IUpdateBusinessPartnerAddress } from './interfaces/update-business-partner-address.interface';
import { PendingAddressResponse } from '../../business-partner-address-pending-sync/interfaces/pending-address.interface';
import { AddressFilters } from '../../business-partner-address-pending-sync/interfaces/address-filters.interface';
import {
  SyncResponse,
  SingleSyncResponse,
} from '../../business-partner-address-pending-sync/interfaces/sync-response.interface';
import BusinessPartnerAttributes from '@albatrosdeveloper/ave-models-package/lib/schemas/business-partners/business-partner/business-partner.entity';
const apiUrl = env.API_BUSINESS_PARTNER;
const moduleName = 'business_partner_address';
const integratorUrl = env.API_AVE_INTEGRATOR_URL + 'api/'

@Injectable({
  providedIn: 'root',
})
export class BusinessPartnerAddressService extends AbstractService<
  AddressAttributes,
  ICreateBusinessPartnerAddress,
  IUpdateBusinessPartnerAddress
> {
  constructor(http: HttpClient) {
    super(apiUrl, moduleName, http);
  }

  /**
   * Pagination
   * @param options Pagination options
   * @returns Paginated data
   */
  findByBusinessId(businessId: string): Observable<IResult<AddressAttributes>> {
    const API_URL: string = `${apiUrl}api/${moduleName}/${businessId}`;
    return this.http.get<IResult<AddressAttributes>>(API_URL);
  }

  /**
   *
   * @param data
   * @returns
   */
  saveImportAddressFile(data: FormData) {
    const API_URL: string = apiUrl + `api/${moduleName}/upload/update-geolocation`;
    return this.http.post<IRequestResult<AddressAttributes>>(API_URL, data);
  }

  /**
   * Obtiene direcciones pendientes de sincronización con ERP
   * @param filters Filtros opcionales para la búsqueda
   * @returns Observable con las direcciones pendientes
   */
  findPendingAddressesForSync(filters: AddressFilters = {}): Observable<PendingAddressResponse> {
    let params = new HttpParams();

    // Agregar parámetros de array como query params
    if (filters.businessPartnerIds?.length) {
      params = params.append('businessPartnerIds', filters.businessPartnerIds.join(','));
    }
    if (filters.locationOneIds?.length) {
      params = params.append('locationOneIds', filters.locationOneIds.join(','));
    }
    if (filters.locationTwoIds?.length) {
      params = params.append('locationTwoIds', filters.locationTwoIds.join(','));
    }
    if (filters.locationThreeIds?.length) {
      params = params.append('locationThreeIds', filters.locationThreeIds.join(','));
    }
    if (filters.territoryIds?.length) {
      params = params.append('routeIds', filters.territoryIds.join(','));
    }

    // Construir query con buildQuery para filtros complejos y paginación
    const query = buildQuery<BusinessPartnerAttributes>(
      // Búsqueda general con el campo filter - buscar en múltiples campos
      filters.filter ? where('name', Ops.like(filters.filter, 'i')) : noop(),
      filters.filter ? where('externalKeys.code' as keyof BusinessPartnerAttributes, Ops.like(filters.filter, 'i')) : noop(),
      filters.filter ? orWhere('addresses.name' as keyof BusinessPartnerAttributes, Ops.like(filters.filter, 'i')) : noop(),
      filters.filter ? orWhere('addresses.address' as keyof BusinessPartnerAttributes, Ops.like(filters.filter, 'i')) : noop(),
      filters.filter ? orWhere('addresses.reference' as keyof BusinessPartnerAttributes, Ops.like(filters.filter, 'i')) : noop(),
      // Paginación
      page(filters.page ?? 1, filters.pageSize ?? 50)
    );

    // Agregar el filter query como parámetro
    if (filters.filter || filters.page !== undefined || filters.pageSize !== undefined) {
      params = params.set('filter', JSON.stringify(query));
    }


    const API_URL = `${apiUrl}api/${moduleName}/pending-sync-with-erp`;
    return this.http.get<PendingAddressResponse>(API_URL, { params });
  }

  /**
   * Sincroniza múltiples direcciones con ERP
   * @param addressIds Array de IDs de direcciones a sincronizar
   * @returns Observable con el resultado de la sincronización
   */
  syncAddressesWithERP(addressIds: string[]): Observable<HttpResponse<SyncResponse>> {
    const API_URL = `${integratorUrl}business-partner/sendBPAddresses`;
    return this.http.post<SyncResponse>(API_URL, { ids: addressIds }, { observe: 'response' });
  }

  /**
   * Sincroniza una sola dirección con ERP
   * @param addressId ID de la dirección a sincronizar
   * @returns Observable con el resultado de la sincronización
   */
  syncSingleAddressWithERP(addressId: string): Observable<HttpResponse<SingleSyncResponse>> {
    const API_URL = `${integratorUrl}business-partner/sendBPAddresses`;
    return this.http.post<SingleSyncResponse>(API_URL, { ids: [addressId] }, { observe: 'response' });
  }

  /**
   * Actualiza múltiples direcciones con los mismos territorios
   * Por ahora hace múltiples llamadas individuales en paralelo,
   * después se reemplazará por endpoint bulk
   * @param bpId ID del business partner
   * @param updates Array de actualizaciones con addressId y routeIds
   * @returns Observable con array de resultados
   */
  updateMultipleAddressesTerritories(
    bpId: string,
    updates: Array<{ addressId: string; routeIds: { id: string; isPrincipal: boolean }[] }>
  ): Observable<IRequestResult<AddressAttributes>[]> {
    const requests = updates.map((update) =>
      this.updateEmbedded(bpId, update.addressId, { routeIds: update.routeIds })
    );
    return forkJoin(requests);

    const API_URL = `${apiUrl}api/${moduleName}/bulk_routes_address_update`;

    this.http.patch(API_URL, {
      businessPartnerId: bpId,
      addresses: updates
    });
  }
}
